// LoginPassword.jsx
import React, { useState } from 'react';
import { Container, Form, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../services/api';

const LoginPassword = () => {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all fields', {
        position: 'bottom-left',
        autoClose: 3000,
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match', {
        position: 'bottom-left',
        autoClose: 3000,
      });
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters', {
        position: 'bottom-left',
        autoClose: 3000,
      });
      return;
    }

    setLoading(true);
    try {
      await api.put('/api/auth/change-password', {
        currentPassword,
        newPassword,
        confirmPassword,
      });

      toast.success('Password changed successfully!', {
        position: 'bottom-left',
        autoClose: 2000,
      });

      setTimeout(() => {
        navigate('/profile');
      }, 1000);
    } catch (err) {
      const errorMsg =
        err.response?.data?.message || 'Failed to change password';

      if (errorMsg.toLowerCase().includes('current password')) {
        toast.error('Current password is incorrect', {
          position: 'bottom-left',
          autoClose: 4000,
        });
      } else {
        toast.error(errorMsg, {
          position: 'bottom-left',
          autoClose: 4000,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        minHeight: '100vh',
      }}
    >
      <Container fluid className='p-0'>
        {/* Header */}
        <div className='d-flex justify-content-between align-items-center p-3 text-white'>
          <Button
            variant='link'
            onClick={() => navigate(-1)}
            className='text-white p-0'
          >
            <ArrowLeft size={24} />
          </Button>
          <h5 className='mb-0 fw-bold'>Change Password</h5>
          <div style={{ width: '24px' }}></div>
        </div>

        {/* Main Content */}
        <div
          className='bg-white'
          style={{
            borderTopLeftRadius: '25px',
            borderTopRightRadius: '25px',
            minHeight: 'calc(100vh - 60px)',
            padding: '24px',
          }}
        >
          <div style={{ maxWidth: '500px', margin: '0 auto' }}>
            {/* Current Password */}
            <Form.Group className='mb-4'>
              <Form.Label
                style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#212529',
                  marginBottom: '8px',
                }}
              >
                Current Password
              </Form.Label>
              <div style={{ position: 'relative' }}>
                <Form.Control
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder='Enter current password'
                  style={{
                    padding: '12px 45px 12px 16px',
                    fontSize: '15px',
                    borderRadius: '10px',
                    border: '1px solid #e0e0e0',
                    backgroundColor: '#f8f9fa',
                  }}
                />
                <Button
                  variant='link'
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#6c757d',
                    padding: '0',
                    border: 'none',
                    background: 'transparent',
                  }}
                >
                  {showCurrentPassword ? (
                    <EyeOff size={20} />
                  ) : (
                    <Eye size={20} />
                  )}
                </Button>
              </div>
            </Form.Group>

            {/* New Password */}
            <Form.Group className='mb-4'>
              <Form.Label
                style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#212529',
                  marginBottom: '8px',
                }}
              >
                New Password
              </Form.Label>
              <div style={{ position: 'relative' }}>
                <Form.Control
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder='Enter new password'
                  style={{
                    padding: '12px 45px 12px 16px',
                    fontSize: '15px',
                    borderRadius: '10px',
                    border: '1px solid #e0e0e0',
                    backgroundColor: '#f8f9fa',
                  }}
                />
                <Button
                  variant='link'
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#6c757d',
                    padding: '0',
                    border: 'none',
                    background: 'transparent',
                  }}
                >
                  {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </Button>
              </div>
            </Form.Group>

            {/* Confirm New Password */}
            <Form.Group className='mb-4'>
              <Form.Label
                style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#212529',
                  marginBottom: '8px',
                }}
              >
                Confirm New Password
              </Form.Label>
              <div style={{ position: 'relative' }}>
                <Form.Control
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder='Confirm new password'
                  style={{
                    padding: '12px 45px 12px 16px',
                    fontSize: '15px',
                    borderRadius: '10px',
                    border: '1px solid #e0e0e0',
                    backgroundColor: '#f8f9fa',
                  }}
                />
                <Button
                  variant='link'
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#6c757d',
                    padding: '0',
                    border: 'none',
                    background: 'transparent',
                  }}
                >
                  {showConfirmPassword ? (
                    <EyeOff size={20} />
                  ) : (
                    <Eye size={20} />
                  )}
                </Button>
              </div>
            </Form.Group>

            {/* Submit Button */}
            <Button
              className='w-100 py-3 fw-semibold'
              onClick={handleSubmit}
              disabled={loading}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                fontSize: '16px',
              }}
            >
              {loading ? 'Changing Password...' : 'Change Password'}
            </Button>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default LoginPassword;
